<template>
  <ContentHeader title="Settings" :learn-more-ids="['company-settings']" />
  <PageBody>
    <div :class="$style['settings-grid']">
      <section :class="$style['settings-left']">
        <header :class="$style['section-header']">
          <p :class="$style['section-eyebrow']">Workspace preferences</p>
        </header>

        <div v-if="showSettings" :class="$style['settings-list']">
          <div :class="$style['setting-item']">
            <div :class="$style['setting-text']">
              <label for="all-sessions-switch" :class="$style['setting-label']">
                Users see all sessions
              </label>
              <p :class="$style['setting-desc']">
                Show every PI Session to every user, regardless of team.
              </p>
            </div>
            <BaseSwitch
              id="all-sessions-switch"
              data-testid="all-sessions-switch"
              :disabled="isSaving"
              :model-value="usersSeeAllSessions"
              @update:model-value="handleAllSessionsChange"
            />
          </div>

          <div
            :class="[$style['setting-item'], autoLogoutEnabled && $style['setting-item-grouped']]"
          >
            <div :class="$style['setting-text']">
              <label for="automatic-logout-switch" :class="$style['setting-label']">
                Automatic logout
              </label>
              <p :class="$style['setting-desc']">Sign users out after a period of inactivity.</p>
            </div>
            <BaseSwitch
              id="automatic-logout-switch"
              data-testid="automatic-logout-switch"
              :disabled="isSaving"
              :model-value="autoLogoutEnabled"
              @update:model-value="handleAutoLogoutEnabled"
            />
          </div>

          <div v-if="autoLogoutEnabled" :class="$style['logout-row']">
            <div :class="$style['setting-inline']">
              <label for="logout-after-numeric" :class="$style['inline-label']">
                Log out after
              </label>
              <BaseNumeric
                id="logout-after-numeric"
                v-model="autoLogoutAfter"
                :class="$style['logout-numeric']"
                data-testid="logout-after-numeric"
                min="5"
                :maxlength="5"
                :error="!!logoutAfterError"
                @update:model-value="isDirty = true"
              />
              <span :class="$style['inline-suffix']">minutes</span>
            </div>
            <p v-if="logoutAfterError" :class="$style['inline-error']" role="alert">
              {{ logoutAfterError }}
            </p>
          </div>
        </div>

        <div :class="$style['save-wrap']">
          <FeedbackButton
            :disabled="isSaveButtonDisabled || !isFormValid"
            success-message="Settings saved successfully."
            @click="saveSettings"
          >
            Save Changes
          </FeedbackButton>
        </div>
      </section>

      <section :class="$style['settings-right']">
        <header :class="$style['section-header']">
          <h2 :class="$style['section-title']">Audit Log</h2>
          <p :class="$style['section-eyebrow']">Export activity history</p>
        </header>

        <form :class="$style['audit-form']" autocomplete="off" @submit.prevent>
          <div :class="$style['date-grid']">
            <div :class="$style['date-field']">
              <label for="start-date-input" :class="$style['field-label']">Start</label>
              <input
                id="start-date-input"
                v-model="auditHistory.start.value"
                type="datetime-local"
                :max="auditHistory.max"
                :class="[
                  $style['date-input'],
                  { [$style['date-error']]: auditHistory.start.error },
                ]"
              />
              <p v-if="auditHistory.start.error" :class="$style['error']" role="alert">
                {{ auditHistory.start.error }}
              </p>
            </div>

            <div :class="$style['date-field']">
              <label for="end-date-input" :class="$style['field-label']">End</label>
              <input
                id="end-date-input"
                v-model="auditHistory.end.value"
                type="datetime-local"
                :max="auditHistory.max"
                :class="[$style['date-input'], { [$style['date-error']]: auditHistory.end.error }]"
              />
              <p v-if="auditHistory.end.error" :class="$style['error']" role="alert">
                {{ auditHistory.end.error }}
              </p>
            </div>
          </div>

          <div :class="$style['export-actions']">
            <SplitButton
              :class="$style['export-button']"
              :label="exportButtonLabel"
              menu-label="Choose export format"
              :disabled="exportingAuditLogs"
              @click="exportAuditLogs(auditFormat)"
            >
              <!-- eslint-disable vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/interactive-supports-focus -->
              <li
                tabindex="-1"
                role="menuitem"
                class="list-item"
                @click="selectAuditFormat('xlsx')"
              >
                Excel (.xlsx)
              </li>
              <li tabindex="-1" role="menuitem" class="list-item" @click="selectAuditFormat('csv')">
                CSV (.csv)
              </li>
            </SplitButton>
          </div>
          <p v-if="exportAuditLogsError" :class="$style['error']" role="alert">
            {{ exportAuditLogsError }}
          </p>
        </form>
      </section>
    </div>

    <div v-if="!serverInfo.isCloud && isAdmin" :class="$style['on-prem-section']">
      <div :class="$style['tar-row']">
        <p>Generate and download support TAR file (send this to rentouch for debug purposes).</p>
        <BaseButton size="small" :loading="isGettingTar" @click="getArchive">Get-TAR</BaseButton>
      </div>
      <p v-if="getTarError != ''" :class="$style['error']" role="alert">
        {{ getTarError }}
      </p>
    </div>

    <div v-if="!serverInfo.isCloud && isAdmin" :class="$style['license-section']">
      <h2 :class="$style['section-title']">License Activation</h2>
      <div :class="$style['license-grid']">
        <div>
          <label for="license-string-textarea" :class="$style['field-label']">
            Enter a valid license below and press "Update"
          </label>
          <textarea
            id="license-string-textarea"
            v-model="licenseString"
            :class="[
              $style['license-textarea'],
              { [$style['textarea-error']]: hasLicenseStringError },
            ]"
            @keyup="validateLicenseString()"
          />
          <p v-if="hasLicenseStringError" :class="$style['error']" role="alert">
            {{ licenseStringError }}
          </p>
          <BaseButton size="small" @click="updateLicense">Update</BaseButton>

          <p :class="$style['renew-info']">
            To renew your license mail your ServerID<br />
            [{{ serverInfo.serverID }}] to <br />
            <a
              :href="`mailto:livesupport@piplanning.io?subject=License renewal for ${serverInfo.serverID}`"
            >
              livesupport@piplanning.io
            </a>
          </p>

          <div :class="$style['release-info']">
            <p><span>Released on:</span>{{ serverInfo.buildDateFormatted }}</p>
            <p><span>Onprem-version:</span>{{ serverInfo.onpremVersion }}</p>
            <p><span>ServerID:</span>{{ serverInfo.serverID }}</p>
          </div>
        </div>
        <div>
          <p :class="$style['license-heading']">Current license information</p>
          <p><span>Number of teams:</span>{{ licenseInfo.numberOfTeams }}</p>
          <p><span>Valid until:</span>{{ licenseInfo.valid_until }}</p>
          <p><span>Created:</span></p>
          <p><span>Organization:</span>{{ company }}</p>
        </div>
      </div>
    </div>
  </PageBody>

  <UnsavedChangesGuard :dirty="isDirty" :save="saveSettings" />
</template>

<script>
import { mapState } from "pinia";

import BaseButton from "@/components-ng/BaseButton.vue";
import BaseNumeric from "@/components-ng/BaseNumeric.vue";
import BaseSwitch from "@/components-ng/BaseSwitch.vue";
import ContentHeader from "@/components-ng/ContentHeader.vue";
import FeedbackButton from "@/components-ng/FeedbackButton.vue";
import PageBody from "@/components-ng/PageBody.vue";
import SplitButton from "@/components-ng/SplitButton.vue";
import UnsavedChangesGuard from "@/components-ng/UnsavedChangesGuard.vue";
import { isCloud } from "@/environment";
import { CompanyHandler } from "@/handlers/companyHandler";
import { LicenseHandler } from "@/handlers/licenseHandler";
import { ServerHandler } from "@/handlers/serverHandler";
import { SessionsHandler } from "@/handlers/sessionsHandler";
import { SupportHandler } from "@/handlers/supportHandler";
import { UserHandler } from "@/handlers/userHandler";
import { initInactivityTimeout } from "@/server";
import AuditLogService from "@/services/auditlog.service";
import { useAppStore } from "@/stores";
import { createHandlerContext } from "@/stores/context";
import { downloadFile } from "@/utils";
import { toDateString, toInputDateTimeString } from "@/utils/dateFormat";
import { dateToUnixTimestamp, unixTimestampToDate } from "@/utils/dates";

const AUTO_LOGOUT_MIN = 5;

export default {
  name: "SettingsPage",
  components: {
    BaseButton,
    BaseSwitch,
    BaseNumeric,
    ContentHeader,
    FeedbackButton,
    PageBody,
    UnsavedChangesGuard,
    SplitButton,
  },
  data() {
    const today = new Date();
    const auditStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 2);
    const now = toInputDateTimeString(today);

    return {
      isDirty: false,
      isSaving: false,
      autoLogoutEnabled: false,
      autoLogoutAfter: 0,
      licenseString: "",
      hasLicenseStringError: false,
      licenseStringError: "",
      licenseInfo: {},
      serverInfo: { isCloud: true },
      getTarError: "",
      isGettingTar: false,
      usersSeeAllSessions: false,
      auditHistory: {
        start: { value: toInputDateTimeString(auditStart), error: "" },
        end: { value: now, error: "" },
        max: now,
      },
      exportAuditLogsError: "",
      exportingAuditLogs: false,
      auditFormat: "xlsx",
      exportProgress: null,
    };
  },
  computed: {
    ...mapState(useAppStore, ["company", "session", "user", "isAdmin"]),
    auditExportLabel() {
      return `Export Logs (${this.auditFormat === "csv" ? "CSV" : "Excel"})`;
    },
    exportButtonLabel() {
      if (!this.exportingAuditLogs) return this.auditExportLabel;
      const p = this.exportProgress;
      if (!p) return "Downloading…";
      if (p.phase === "finalize") return "Building File…";
      // Pad to a fixed 3-char width with figure spaces (U+2007, a digit-wide
      // space) so the % counter doesn't change width as it climbs 0→100; paired
      // with tabular-nums on the button so the digits themselves are fixed width.
      const pct = String(p.total ? Math.round((p.done / p.total) * 100) : 100).padStart(3, " ");
      return p.phase === "build" ? `Building File… ${pct}%` : `Downloading… ${pct}%`;
    },
    supportHandler() {
      return new SupportHandler();
    },
    serverHandler() {
      return new ServerHandler();
    },
    userHandler() {
      return new UserHandler();
    },
    sessionsHandler() {
      return new SessionsHandler(createHandlerContext());
    },
    companyHandler() {
      return new CompanyHandler();
    },
    licenseHandler() {
      return new LicenseHandler();
    },
    showSettings() {
      return this.isAdmin;
    },
    isSaveButtonDisabled() {
      if (this.autoLogoutEnabled && !this.autoLogoutAfter) {
        return true;
      }
      return this.isSaving || !this.isDirty;
    },
    isFormValid() {
      if (this.autoLogoutEnabled) {
        return this.autoLogoutAfter >= 5;
      }
      return true;
    },
    logoutAfterError() {
      if (!this.autoLogoutEnabled) return "";
      if (this.autoLogoutAfter === null || this.autoLogoutAfter === undefined) {
        return "Please enter a number.";
      }
      if (this.autoLogoutAfter < AUTO_LOGOUT_MIN) {
        return `Minimum is ${AUTO_LOGOUT_MIN} minutes.`;
      }
      return "";
    },
  },
  async mounted() {
    this.getSettings();

    if (this.isAdmin) {
      await this.getLicense();
      await this.getLicenseDetails();
      await this.fetchServerInfo();

      const buildDate = unixTimestampToDate(this.serverInfo.buildDate);
      this.serverInfo.buildDateFormatted = buildDate ? toDateString(buildDate) : "";
      this.getUsersSeeAllSessions();
    }
  },
  methods: {
    handleAutoLogoutEnabled(value) {
      this.isDirty = true;
      this.autoLogoutEnabled = value;

      if (!value) {
        this.autoLogoutAfter = 0;
      } else if (!this.autoLogoutAfter || this.autoLogoutAfter < AUTO_LOGOUT_MIN) {
        this.autoLogoutAfter = AUTO_LOGOUT_MIN;
      }
    },
    handleAllSessionsChange(value) {
      this.isDirty = true;
      this.usersSeeAllSessions = value;
    },

    async saveSettings() {
      // Enforced here so BOTH the Save button (disabled on !isFormValid) and the guard
      // honor it; throwing on invalid makes the guard cancel navigation.
      if (!this.isFormValid) {
        throw new Error("Settings are invalid.");
      }
      this.isSaving = true;

      try {
        await Promise.all([
          this.setUsersSeeAllSessions(this.usersSeeAllSessions),
          this.setAutoLogoutAfter(this.autoLogoutAfter),
        ]);
        this.isDirty = false;
      } finally {
        this.isSaving = false;
      }
    },

    async setUsersSeeAllSessions(value) {
      await this.sessionsHandler.setUsersSeeAllSessions(value);
    },

    async setAutoLogoutAfter(value) {
      const settings = { autoLogoutAfter: value };
      await this.companyHandler.setCompanySettings(settings);

      // (re)set the inactivity timeout to the new value
      initInactivityTimeout();
    },

    async getSettings() {
      const res = await this.companyHandler.getCompanySettings();
      if (!res.success) return;

      const fromApi = res.data.autoLogoutAfter;
      this.autoLogoutEnabled = fromApi > 0;
      this.autoLogoutAfter = this.autoLogoutEnabled ? Math.max(fromApi, AUTO_LOGOUT_MIN) : 0;
    },

    async fetchServerInfo() {
      this.serverInfo = await this.serverHandler.getServerInfo();
    },
    async getLicense() {
      if (!isCloud) {
        this.licenseString = await this.licenseHandler.getOnPremKey();
      }
    },
    async getUsersSeeAllSessions() {
      this.usersSeeAllSessions = await this.sessionsHandler.getUsersSeeAllSessions();
      this.loadingUsersSeeAllSessions = false;
    },

    async getLicenseDetails() {
      const res = await this.licenseHandler.getCompanyLicense();
      if (!res.success) return;
      this.licenseInfo = res.data;

      this.licenseInfo.valid_until = toDateString(this.licenseInfo.lastValidDay);
    },
    async getArchive() {
      this.isGettingTar = true;
      this.getTarError = "";
      try {
        const blob = await this.supportHandler.getTar();
        downloadFile("support.tar.gz", blob);
      } catch (err) {
        this.getTarError = err?.message || String(err);
      } finally {
        this.isGettingTar = false;
      }
    },
    validateLicenseString() {
      if (this.licenseString.length === 0) {
        this.hasLicenseStringError = true;
        this.licenseStringError = "License string is required.";
      } else {
        this.hasLicenseStringError = false;
        this.licenseStringError = "";
      }
    },
    async updateLicense() {
      this.validateLicenseString();
      if (this.hasLicenseStringError) return;

      const res = await this.licenseHandler.updateOnPremKey(this.licenseString);
      if (res.success) {
        await this.getLicenseDetails();
      } else {
        this.hasLicenseStringError = true;
        this.licenseStringError = "Invalid license string";
      }
    },
    async exportAuditLogs(format = "xlsx") {
      this.validateAuditDateValue("start");
      this.validateAuditDateValue("end");
      this.validateAuditDateInterval();
      if (this.auditHistory.start.error || this.auditHistory.end.error) return;

      this.exportAuditLogsError = "";
      this.exportingAuditLogs = true;
      this.exportProgress = null;
      const start = new Date(this.auditHistory.start.value);
      const end = new Date(this.auditHistory.end.value);
      const startFormatted = toDateString(start).replace(/[^0-9]/g, "");
      const endFormatted = toDateString(end).replace(/[^0-9]/g, "");
      const useCsv = format === "csv";
      const fileName = `log-${startFormatted}-${endFormatted}.${useCsv ? "csv" : "xlsx"}`;
      try {
        const startTs = dateToUnixTimestamp(start);
        const endTs = dateToUnixTimestamp(end);
        const blob = await AuditLogService.exportAuditLogs(startTs, endTs, format, (progress) => {
          this.exportProgress = progress;
        });
        downloadFile(fileName, blob);
      } catch (err) {
        this.exportAuditLogsError = `Could not export audit logs: ${await this.auditLogErrorText(err)}`;
      } finally {
        this.exportingAuditLogs = false;
        this.exportProgress = null;
      }
    },
    selectAuditFormat(format) {
      this.auditFormat = format;
      return this.exportAuditLogs(format);
    },
    async auditLogErrorText(err) {
      let raw = err?.response?.raw;
      if (raw instanceof Blob) {
        raw = await raw.text();
      }
      if (typeof raw === "string") {
        try {
          return JSON.parse(raw).detail ?? raw;
        } catch {
          return raw;
        }
      }
      return err?.userMessage || err?.message || "unknown error";
    },
    validateAuditDateValue(key) {
      this.auditHistory[key].error =
        this.auditHistory[key].value === "" ? `Please enter the ${key} date` : "";
    },
    validateAuditDateInterval() {
      if (this.auditHistory.start.value && this.auditHistory.end.value) {
        const isValidInterval =
          new Date(this.auditHistory.start.value) <= new Date(this.auditHistory.end.value);
        this.auditHistory.start.error = isValidInterval
          ? ""
          : "The start date has to be sooner in time than the end date";
        this.auditHistory.end.error = isValidInterval
          ? ""
          : "The end date has to be later in time than the start date";
      }
    },
  },
};
</script>

<style lang="scss" module>
@use "@/colors" as c;

.settings-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 0;
  align-items: stretch;
  max-width: 1100px;

  @media (width <= 900px) {
    grid-template-columns: 1fr;
  }
}

.settings-left {
  min-width: 0;
  padding-right: 48px;

  @media (width <= 900px) {
    padding-right: 0;
  }
}

.settings-right {
  min-width: 0;
  padding-left: 48px;
  border-left: 1px solid c.$border-subtle;

  @media (width <= 900px) {
    padding-left: 0;
    padding-top: 32px;
    margin-top: 32px;
    border-left: none;
    border-top: 1px solid c.$border-subtle;
  }
}

.section-header {
  margin-bottom: 24px;
}

.section-eyebrow {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: c.$text-subtle;
  margin: 0 0 4px;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: c.$text;
  letter-spacing: -0.01em;
}

.settings-list {
  display: flex;
  flex-direction: column;
}

.setting-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 16px 0;
  border-bottom: 1px solid c.$border-subtle;

  &:first-child {
    padding-top: 0;
  }
}

.setting-item-grouped {
  border-bottom: none;
  padding-bottom: 8px;
}

.setting-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.setting-label {
  font-size: 14px;
  font-weight: 500;
  color: c.$text;
}

.setting-desc {
  font-size: 13px;
  color: c.$text-subtle;
  margin: 0;
  line-height: 1.4;
}

.setting-inline {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 0 16px;
  border-bottom: 1px solid c.$border-subtle;
}

.inline-label {
  font-size: 13px;
  color: c.$text-subtle;
}

.inline-suffix {
  font-size: 13px;
  color: c.$text-subtle;
}

.logout-numeric {
  width: 72px;
}

.logout-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 0 16px;
  border-bottom: 1px solid c.$border-subtle;

  .setting-inline {
    padding: 0;
    border-bottom: none;
  }
}

.inline-error {
  margin: 0;
  font-size: 12px;
  color: c.$brand-danger;
}

.save-wrap {
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
}

.audit-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.date-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.date-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.field-label {
  font-size: 12px;
  font-weight: 500;
  color: c.$text-subtle;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.date-input {
  width: 100%;
  font-size: 13px;
  padding: 8px 10px;
  border: 1px solid c.$border;
  border-radius: 6px;
  outline: none;
  box-sizing: border-box;
  background: c.$white;
  color: c.$text;
  transition: border-color 0.15s ease;

  &:focus {
    border-color: c.$blue;
  }
}

.date-error {
  border-color: c.$red;
  color: c.$red;
}

.export-actions {
  align-self: flex-start;
  margin-top: 4px;
}

// Fixed-width digits so the progress counter (e.g. "Building File… 50%") doesn't
// shift the button width as the percentage climbs.
.export-button {
  font-variant-numeric: tabular-nums;
}

.error {
  font-size: 12px;
  color: c.$red;
  margin: 4px 0 0;
}

// On-prem sections

.on-prem-section {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid c.$border;
}

.tar-row {
  display: flex;
  align-items: center;
  gap: 16px;

  p {
    font-size: 14px;
    color: c.$text-subtle;
    margin: 0;
  }
}

.license-section {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid c.$border;
}

.license-grid {
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;

  > div {
    flex: 1 1 300px;
  }

  p {
    font-size: 14px;
    margin: 4px 0;

    span {
      display: inline-block;
      width: 180px;
      color: c.$text-subtle;
    }
  }
}

.license-heading {
  font-weight: 600;
  margin-bottom: 12px !important;
}

.license-textarea {
  width: 100%;
  height: 200px;
  margin: 12px 0;
  padding: 8px;
  font-size: 14px;
  border: 1px solid c.$border;
  border-radius: 4px;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    border-color: c.$brand-primary;
    outline: none;
  }
}

.textarea-error {
  border-color: c.$icon-danger-color;
}

.renew-info {
  margin-top: 16px;
  line-height: 1.5;
  font-size: 14px;
}

.release-info {
  margin-top: 24px;
}
</style>
